import {
  CommonHelperFunctions,
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IComponent,
  IDesign,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IProject,
  IPropertyDefinition,
  IScreenDesign,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import { SymbolPicker } from "./symbol-picker";

const PHOTO_QUALITY_OPTIONS: KuikaSelectboxOption[] = [
  { key: "default", value: "default", label: "Default" },
  { key: "low", value: "low", label: "Low" },
  { key: "medium", value: "medium", label: "Medium" },
  { key: "high", value: "high", label: "High" }
];

export interface IPhotoTakeymbolsGroupState {}

export interface IPhotoTakeymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

class PhotoTakeymbolsGroup extends PureSdComponent<
  IPhotoTakeymbolsGroupProps & IWithSDProps & IWithContextProp,
  IPhotoTakeymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: IPhotoTakeymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  handleComponentSelect = (value: string, parameterName: string): void => {
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = this.props.parameters.find((element) => element.name === parameterName)!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = value.length ? value : "default";
    _parameter.symbol.category = ESymbolCategory.fixed;
    const valuesToUpdate = {
      actionParameters: params.filter((actionParameter) => actionParameter.name !== parameterName)
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, undefined);
  };

  getSelectValue = (): string | undefined => {
    const qualityParameter: IActionParameter | undefined = this.props.parameters.find(
      (element: IActionParameter): boolean => element.name === "quality"
    );
    if (!qualityParameter?.symbol) {
      return undefined;
    }
    const fixedValue: unknown = qualityParameter.symbol.fixedValue;
    return typeof fixedValue === "string" && fixedValue.length > 0 ? fixedValue : "default";
  };

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const valueType = "Boolean";
    symbol.targetType = valueType;
    parameter.Typename = valueType;
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = parameter;
    _parameter.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params.filter((actionParameter) => actionParameter.name !== parameter.name)
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty,
    project: IProject
  ): IPropertyDefinition | undefined => {
    if (project.componentSetObj && project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentById(
        diComponent?.id,
        project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          const result: IPropertyDefinition = {
            description: property.description,
            hasOptions: property.hasOptions,
            isRequired: property.isRequired,
            isTechnical: property.isTechnical,
            // isEvent: property.isEvent,
            isVisibleInFieldPicker: property.isVisibleInFieldPicker,
            isValueProp: property.isValueProp,
            isOptionsProp: property.isOptionsProp,
            isLanguageOptionsProp: property.isLanguageOptionsProp,
            isEditabilityProp: property.isEditabilityProp,
            isVisibleProp: property.isVisibleProp,
            isAdvanced: property.isAdvanced,
            isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
            isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
            isSupportMultiLanguage: property.isSupportMultiLanguage,
            appSupportType: property.appSupportType,
            isDeprecated: property.isDeprecated,
            name: property.name,
            options: property.options,
            tagName: property.tagName,
            type: property.type,
            typeOptions: property.typeOptions,
            defaultValue: property.symbol?.category === ESymbolCategory.fixed ? property.symbol.fixedValue : "",
            isChartOptionsProp: property.isChartOptionsProp,
            isTimelineOptionsProp: property.isTimelineOptionsProp,
            isChatOptionsProp: property.isChatOptionsProp,
            isCalendarOptionsProp: property.isCalendarOptionsProp,
            isMenuOptionsProp: property.isMenuOptionsProp,
            isBreadcrumbOptionsProp: property.isBreadcrumbOptionsProp,
            isRadialChartOptionsProp: property.isRadialChartOptionsProp,
            isRadialChartLevelOptionsProp: property.isRadialChartLevelOptionsProp,
            isMixedChartOptionsProp: property.isMixedChartOptionsProp,
            isMixedChartLineOptionsProp: property.isMixedChartLineOptionsProp,
            isMixedChartPointOptionsProp: property.isMixedChartPointOptionsProp,
            isMixedChartBarOptionsProp: property.isMixedChartBarOptionsProp,
            isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
            isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
            isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
            isTreeOptionsProp: property.isTreeOptionsProp,
            inVisibleStyles: property.inVisibleStyles,
            hideFixedValue: property.hideFixedValue
          };
          return result;
        }
      }
    }
  };

  renderComponentList = (): JSX.Element => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    this.fillAllDisplayItemsRecursive(sd, allDi);
    allDi = allDi.sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName"));
    allDi = allDi.filter((a) => a.id > 0);
    const editPhotoParameter = this.props.parameters.find((element) => element.name === "editPhoto")!;

    let dontShowEditPhoto: boolean = false;
    if (this.props.action.label === "PhotoFromGallery" || this.props.action.label === "PhotosFromGallery") {
      dontShowEditPhoto = this.props.sd.project.projectType === EProjectType.Desktop;
    }

    return (
      <>
        {!dontShowEditPhoto && (
          <>
            <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
              Edit Photo
            </div>
            <SymbolPicker
              action={this.props.action}
              changeType="onBlur"
              symbol={editPhotoParameter.symbol}
              valueType="Boolean"
              targetCardinality={ECardinality.One}
              onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                this.handleSelectSymbolPickerValue(editPhotoParameter!, symbol, changeReason)
              }
              bindingParameter={editPhotoParameter}
              bindingLabel="Edit Photo"
            ></SymbolPicker>
          </>
        )}
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Photo Quality
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={this.getSelectValue() ?? null}
            onChange={(val: string): void => {
              this.handleComponentSelect(val, "quality");
            }}
            className="kuika_add_actions_inputs_size"
            options={PHOTO_QUALITY_OPTIONS}
          />
        </div>
      </>
    );
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        if (!di.children[i].friendlyName) {
          di.children[i].friendlyName = di.children[i].id.toString();
        }
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        if (!sd.displayItems[i].friendlyName) {
          sd.displayItems[i].friendlyName = sd.displayItems[i].id.toString();
        }
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  render() {
    return <div style={{ width: "100%" }}>{this.renderComponentList()}</div>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const photoTakeymbolsGroup = withContext<IPhotoTakeymbolsGroupProps>(
  withSD<IPhotoTakeymbolsGroupProps>(PhotoTakeymbolsGroup)
);
export { photoTakeymbolsGroup as PhotoTakeymbolsGroup };
